<template>
	<ValidationForm
		#default="{ handleSubmit }"
		tag="div"
	>
		<ViewLayout>
			<template #header-title>
				CS Portal Configuration
			</template>
			<template #header-after>
				<Tabs
					:tab.sync="tab"
					:tabs="tabs"
				/>
			</template>
			<template #content>
				<TabItems
					:tab="tab"
					:tabs="tabs"
				>
					<template #general>
						<CsPortalConfigGeneral
							:configuration.sync="configuration"
							:response-channels.sync="configuration.responseChannels"
							:source-codes.sync="configuration.sourceCodes"
							:user-full-permissions="userFullPermissions"
							@update-configuration="updateConfiguration"
						/>
					</template>
					<template #search-fields>
						<CsPortalConfigSearchFields
							:configuration.sync="configuration"
							:available-fields="availableFields"
							:search-fields.sync="configuration.dataSubjectSearchFieldsList"
							:user-full-permissions="userFullPermissions"
							@update-configuration="updateConfiguration"
						/>
					</template>
					<template #contact-details-fields>
						<CsPortalConfigContactDetailsFields
							:configuration.sync="configuration"
							:contact-details-fields.sync="configuration.contactDetailsFields"
							:show-post-code-lookup.sync="configuration.basicConfig.showPostCodeLookup"
							:available-fields="availableFields"
							:user-full-permissions="userFullPermissions"
							@update-configuration="updateConfiguration"
						/>
					</template>
					<template #preferences>
						<CsPortalConfigPreferences
							:parent-groups="configuration.preferenceGroups.parentGroups"
							:available-fields="availableFields"
							:user-full-permissions="userFullPermissions"
							@update:parentGroups="handleParentGroupsUpdate($event)"
						/>
					</template>
				</TabItems>
			</template>
			<template #footer>
				<PageActionRow v-if="userFullPermissions">
					<template #actions>
						<v-spacer />
						<PrimaryActionButton @click="handleSubmit(save)">
							Save
						</PrimaryActionButton>
					</template>
				</PageActionRow>
			</template>
		</ViewLayout>
	</ValidationForm>
</template>
<script>
import { mapGetters } from 'vuex'
import ValidationForm from '../../../../../shared/components/validation-form.vue'
import ViewLayout from '../../../../../shared/layouts/view-layout.vue'
import TabItems from '../../../../../shared/components/tab-items.vue'
import Tabs from '../../../../../shared/components/tabs.vue'
import CsPortalConfigGeneral from './cs-portal-config-general/cs-portal-config-general.vue'
import { waitForLoad, configuration, searchFieldFilter, showPostcodeLookup } from '../../../../../shared/state/configuration.js'
import PageActionRow from '../../../../../shared/components/page-action-row.vue'
import PrimaryActionButton from '../../../../../shared/components/primary-action-button.vue'
import CsPortalConfigSearchFields from './cs-portal-config-search-fields.vue'
import CsPortalConfigContactDetailsFields from './cs-portal-config-contact-details-fields.vue'
import CsPortalConfigPreferences from './cs-portal-config-preferences/cs-portal-config-preferences.vue'
import { getStandakFields, putConfigurationApi, getConfigurationApi } from '../../../../../shared/utils/api/configuration.js'
import { CS_ADMINISTRATION_MODULE_FULL_ACCESS } from '../../../../../shared/permissions/admin-portal-permissions.js'
import { showSnackbar } from '../../../../../shared/state/snackbar.js'
import { setFormChangedValue } from '../../../../../shared/state/validation-observers.js'

export default {
	components: {
		CsPortalConfigPreferences,
		CsPortalConfigContactDetailsFields,
		CsPortalConfigSearchFields,
		PrimaryActionButton,
		PageActionRow,
		CsPortalConfigGeneral,
		Tabs,
		TabItems,
		ViewLayout,
		ValidationForm
	},
	setup () {
		return {
			showSnackbar,
			showPostcodeLookup,
			waitForLoad,
			setFormChangedValue
		}
	},
	data () {
		return {
			tab: 'general',
			tabs: [
				{ slot: 'general', title: 'General', icon: 'mdi-cog-outline' },
				{ slot: 'search-fields', title: 'Search Fields', icon: 'mdi-text-search' },
				{ slot: 'contact-details-fields', title: 'Contact Details Fields', icon: 'mdi-card-account-mail-outline' },
				{ slot: 'preferences', title: 'Preferences', icon: 'mdi-order-bool-ascending-variant' }
			],
			configuration: {
				basicConfig: {},
				dataSubjectSearchFieldsList: [],
				preferenceGroups: {},
				headerFooterCombinations: [],
				responseChannels: [],
				sourceCodes: [],
				contactDetailsFields: []
			},
			availableFields: []
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		userFullPermissions () {
			return this.productAreaPermission(CS_ADMINISTRATION_MODULE_FULL_ACCESS)
		}
	},
	async created () {
		await waitForLoad()
		this.configuration = JSON.parse(JSON.stringify(configuration.value))
		const { data: availableFields } = await getStandakFields()
		this.availableFields = availableFields
		const { data: basicConfig } = await getConfigurationApi()
		this.configuration = basicConfig
	},
	methods: {
		searchFieldFilter,
		updateConfiguration (configuration) {
			this.configuration = configuration
			this.manuallyTriggerFormChanged(true)
		},
		handleParentGroupsUpdate (parentGroups) {
			this.updateConfiguration({
				...this.configuration,
				preferenceGroups: {
					...this.configuration.preferenceGroups,
					parentGroups
				}
			})
		},
		async save () {
			if (this.configuration.dataSubjectSearchFieldsList.filter(({ includeInSearchResults }) => includeInSearchResults).length === 0) {
				showSnackbar({ text: 'At least one field must be visible in the search results', color: 'red' })
			} else if (this.configuration.preferenceGroups.parentGroups.length === 0) {
				showSnackbar({ text: 'At least one preference group must be configured', color: 'red' })
			} else {
				await putConfigurationApi(this.configuration)
				await waitForLoad()
				const { data: freshConfig } = await getConfigurationApi()
				this.configuration = JSON.parse(JSON.stringify(freshConfig))
				this.manuallyTriggerFormChanged(false)
				showSnackbar('Your changes have been saved')
			}
		},
		manuallyTriggerFormChanged (value) {
			setFormChangedValue(value)
		}
	}
}
</script>
